import { autoinject, bindable, DOM } from "aurelia-framework";
import { transient } from "aurelia-dependency-injection";
import { Guid } from "../../../utilities/guid";
import { computedFrom } from "aurelia-binding";
import { Reflection } from "../../../utilities/reflection";
import { BindingSignaler } from "aurelia-templating-resources";

@autoinject
@transient()
export class TextBox {

    public static FilterModes = {
        blur: "blur",
        focus: "focus",
        change: "change"
    };

    private _vm = this;

    public isPasswordVisible: boolean = false;
    public isAttached: boolean;
    public previousValue: string;
    public currentValue: string;
    public decimalSeparator: string = ".";
    public thousandSeparator: string = ",";
    public valueSignaler: string;
    public input: HTMLInputElement;
    public diffLength: number = 0;

    @bindable
    public label: string = "";

    @bindable
    public stopPropagationOnClick: boolean = true;

    @bindable
    public startLabel: string = "";

    @bindable
    public filter: (value: string) => string;

    @bindable
    public endLabel: string = "";

    @bindable
    public readonly: boolean = false;

    @bindable
    public disabled: boolean = false;

    @bindable
    public type: string = "text";

    @bindable
    public nullWhenEmpty: boolean = false;

    @bindable
    public value: string = "";

    @bindable
    public maxLength: number = 99999;

    @bindable
    public filterMode: string = TextBox.FilterModes.change;

    @bindable
    public placeholder: string = "";

    @bindable
    public culture: string = "";

    @bindable
    public pattern: string = "";

    @bindable
    public decimalPlaces: number = 2;

    @bindable
    public info: string = "";

    @bindable
    public min: number = 0;

    @bindable
    public max: number = 2147483647;

    @bindable
    public identifier: string;

    @bindable
    public allowNegative: boolean = false;

    @bindable
    public tab: number;

    @bindable
    public isFocused: boolean;

    @bindable
    public enableTogglePassword: boolean = false;

    @bindable
    public autoComplete: string = "new-password";

    @bindable
    public useNumberGrouping: boolean = true;

    @bindable
    public startIcon: string;
    
    @bindable
    public canClearText: boolean;

    @bindable
    public showStartLabelInReadonly: boolean = false;

    @bindable
    public size: string = "medium";

    @computedFrom(Reflection.getPropertyPath<TextBox>(x => x._vm))
    public get vm(): TextBox {
        return this._vm;
    }

    @computedFrom(
        Reflection.getPropertyPath<TextBox>(x => x.type),
        Reflection.getPropertyPath<TextBox>(x => x.isPasswordVisible)
    )
    public get internalType(): string {
        switch (this.type) {
            // to show numeric keyboard on mobile
            case "numeric-password":
                if (this.isPasswordVisible) {
                    return "text";
                }
                break;
            case "double":
            case "float":
            case "integer":
            case "digits":
                return "tel";
            case "password":
                if (this.isPasswordVisible) {
                    return "text";
                }
                break;
            default:
                return this.type;
        }
    }

    @computedFrom(
        Reflection.getPropertyPath<TextBox>(z => z.startLabel),
        Reflection.getPropertyPath<TextBox>(z => z.readonly),
        Reflection.getPropertyPath<TextBox>(z => z.value)
    )
    public get canShowStartLabel(): boolean {
        return this.startLabel && (!this.readonly || (this.readonly && this.value != null));
    }

    @computedFrom(
        Reflection.getPropertyPath<TextBox>(z => z.endLabel),
        Reflection.getPropertyPath<TextBox>(z => z.readonly),
        Reflection.getPropertyPath<TextBox>(z => z.value)
    )
    public get canShowEndLabel(): boolean {
        return this.endLabel && (!this.readonly || (this.readonly && this.value != null));
    }

    public constructor(private element: Element, private bindingSignaler: BindingSignaler) {
        this.identifier = Guid.generateGuid();
        this.valueSignaler = "textbox_" + this.identifier;
        this.setDecimalSeparator();
    }

    public attached(): void {
        this.isAttached = true;
        if (this.input) {
            this.input.addEventListener("click", this.onClick, false);
            this.input.addEventListener("keydown", this.onKeyDown, false);
            this.input.addEventListener("cut", this.cancelEvent, false);
            this.input.addEventListener("copy", this.cancelEvent, false);
            this.bindingSignaler.signal(this.valueSignaler);
        }
    }

    public valueChanged(newValue: string, oldValue: string): void {
        this.registerChanges(oldValue, newValue);
        setTimeout(() => {
            this.raiseTextChangedEvent(oldValue, newValue);
        });
    }

    public propertyChanged(property: string): void {
        switch (property) {
            case "decimalPlaces":
            case "type":
            case "readonly":
            case "disabled":
                {
                    if (this.input) {
                        this.bindingSignaler.signal(this.valueSignaler);
                    }
                }
                break;
            case "culture":
                {
                    this.setDecimalSeparator();

                    if (this.input) {
                        this.bindingSignaler.signal(this.valueSignaler);
                    }
                }
                break;
            default:
                break;
        }
    }

    private setDecimalSeparator() {
        const num: number = 1000000.1000000;

        if (!this.culture || this.culture.length == 0) {
            this.culture = window.navigator.language;
        }

        const parts: any[] = Intl.NumberFormat(this.culture)["formatToParts"](num);

        this.decimalSeparator = parts.filter(x => x.type == "decimal")[0].value;
        this.thousandSeparator = parts.filter(x => x.type == "group")[0].value;
    }

    public onFocus($event: Event): void {
        this.isFocused = true;
        const event: CustomEvent = DOM.createCustomEvent("focus", $event);
        this.element.dispatchEvent(event);
        if (this.filterMode.toLowerCase() === TextBox.FilterModes.focus) {
            if (this.filter) {
                this.value = this.filter((<any>$event.target).value);
            }
        }
    }

    public focusTextBox(selectAlltext: boolean = false): void {
        const input: HTMLInputElement = this.input;
        setTimeout(() => {
            input.focus();
            if (selectAlltext) {
                input.select();
            }
        });
    }

    public onBlured($event: Event): void {
        this.isFocused = false;
        this.bindingSignaler.signal(this.valueSignaler);
        if (this.filterMode.toLowerCase() === TextBox.FilterModes.blur) {
            if (this.filter) {
                this.value = this.filter((<any>$event.target).value);
            }
        }
        const event: CustomEvent = DOM.createCustomEvent("blur", $event);
        this.element.dispatchEvent(event);
    }

    public clearField(): void {
        this.value = null;
    }

    public togglePasswordVisibility(): void {
        this.isPasswordVisible = !this.isPasswordVisible;
    }

    public detached(): void {
        if (this.input) {
            this.input.removeEventListener("click", this.onClick, false);
            this.input.removeEventListener("keydown", this.onKeyDown, false);
            this.input.removeEventListener("cut", this.cancelEvent, false);
            this.input.removeEventListener("copy", this.cancelEvent, false);
        }
    }

    private onClick = ($event: Event): void => {
        if ($event && this.stopPropagationOnClick) {
            $event.stopPropagation();
        }
    }

    private onKeyDown = (event: Event): void => {
        const changeTextEvent: CustomEvent = new CustomEvent("key-down", { detail: event, bubbles: true });
        this.element.dispatchEvent(changeTextEvent);
    }

    private cancelEvent = (event: Event): boolean => {
        if (this.type === "numeric-password") {
            this.stopEvent(event);
            return false;
        }
        return true;
    }

    private stopEvent(e): void {
        if (e.stopPropagation) {
            e.stopPropagation();
        }
        if (e.preventDefault) {
            e.preventDefault();
        }
        e.cancelBubble = true;
        e.returnValue = false;
    }

    private registerChanges(oldValue: string, newValue: string): void {
        this.previousValue = oldValue;
        this.currentValue = newValue;
    }

    private raiseTextChangedEvent(oldValue: string, newValue: string): void {
        const paramsEvent: any = { detail: { oldValue: oldValue, newValue: newValue }, bubbles: true };
        const changeTextEvent: CustomEvent = new CustomEvent("textchange", paramsEvent);
        this.element.dispatchEvent(changeTextEvent);
    }

    //Clear text from the textbox
    clearText() {
        this.value = "";
    }
}
